import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  Select,
  Checkbox,
  Button,
  Space,
  Row,
  Col,
  Divider,
  Typography,
  Alert,
  Progress,
  Input,
  Tooltip
} from 'antd';
import {
  FilePdfOutlined,
  DownloadOutlined,
  SettingOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { SeverityLevel } from '../../../types/dashboard/csa.types';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

interface CSARelatorioModalProps {
  visible: boolean;
  onClose: () => void;
  onGenerate: (options: RelatorioOptions) => Promise<void>;
  clienteNome: string;
  loading?: boolean;
  hostsDisponiveis?: string[];
  progresso?: number;
}

export interface RelatorioOptions {
  periodo: {
    inicio: string;
    fim: string;
  };
  severidades: string[];
  incluirDetalhes: boolean;
  incluirGraficos: boolean;
  incluirRecomendacoes: boolean;
  incluirResumoExecutivo: boolean;
  incluirIndice: boolean;
  incluirEstatisticas: boolean;
  formato: 'pdf';
  filtros: {
    host?: string;
    cvssMinimo?: number;
    cveEspecifico?: string;
    portaEspecifica?: string;
    limiteDetalhes?: number;
  };
}

const CSARelatorioModal: React.FC<CSARelatorioModalProps> = ({
  visible,
  onClose,
  onGenerate,
  clienteNome,
  loading = false,
  hostsDisponiveis = [],
  progresso = 0
}) => {
  const [form] = Form.useForm();
  const [opcesAvancadas, setOpcesAvancadas] = useState(false);
  const [periodoPersonalizado, setPeriodoPersonalizado] = useState(false);

  const periodosPreDefinidos = [
    { label: 'Últimos 7 dias', value: 7 },
    { label: 'Últimos 30 dias', value: 30 },
    { label: 'Últimos 90 dias', value: 90 },
    { label: 'Último semestre', value: 180 },
    { label: 'Último ano', value: 365 },
    { label: 'Período personalizado', value: 'custom' }
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (values.periodo && values.periodo[0].isAfter(values.periodo[1])) {
        throw new Error('Data inicial deve ser anterior à data final');
      }

      if (!values.severidades || values.severidades.length === 0) {
        throw new Error('Selecione pelo menos uma severidade');
      }

      const temConteudo = values.incluirDetalhes || 
                         values.incluirGraficos || 
                         values.incluirRecomendacoes || 
                         values.incluirResumoExecutivo;
      
      if (!temConteudo) {
        throw new Error('Selecione pelo menos um tipo de conteúdo para incluir no relatório');
      }

      const options: RelatorioOptions = {
        periodo: {
          inicio: values.periodo[0].format('YYYY-MM-DD'),
          fim: values.periodo[1].format('YYYY-MM-DD')
        },
        severidades: values.severidades || Object.values(SeverityLevel),
        incluirDetalhes: values.incluirDetalhes ?? true,
        incluirGraficos: values.incluirGraficos ?? true,
        incluirRecomendacoes: values.incluirRecomendacoes ?? true,
        incluirResumoExecutivo: values.incluirResumoExecutivo ?? true,
        incluirIndice: values.incluirIndice ?? true,
        incluirEstatisticas: values.incluirEstatisticas ?? true,
        formato: 'pdf',
        filtros: {
          ...(values.host && values.host.trim() !== '' && { host: values.host.trim() }),
          ...(values.cvssMinimo !== undefined && values.cvssMinimo !== null && { cvssMinimo: values.cvssMinimo }),
          ...(values.cveEspecifico && values.cveEspecifico.trim() !== '' && { cveEspecifico: values.cveEspecifico.trim() }),
          ...(values.portaEspecifica && values.portaEspecifica.trim() !== '' && { portaEspecifica: values.portaEspecifica.trim() }),
          limiteDetalhes: values.limiteDetalhes || 100
        }
      };

      await onGenerate(options);
    } catch (error) {
      // Silently handle error
    }
  };

  const handleReset = () => {
    form.resetFields();
    setOpcesAvancadas(false);
    setPeriodoPersonalizado(false);
  };

  const handlePeriodoChange = (value: number | string) => {
    if (value === 'custom') {
      setPeriodoPersonalizado(true);
      return;
    }
    
    setPeriodoPersonalizado(false);
    const dias = value as number;
    form.setFieldsValue({
      periodo: [dayjs().subtract(dias, 'days'), dayjs()]
    });
  };

  useEffect(() => {
    if (!visible) {
      handleReset();
    }
  }, [visible]);

  const hostsLimpos = Array.from(new Set(
    hostsDisponiveis
      .filter(host => host && host.trim() !== '' && host.toLowerCase() !== 'unknown')
      .map(host => host.trim())
  )).sort();

  return (
    <Modal
      title={
        <Space>
          <FilePdfOutlined />
          Gerar Relatório CSA
          <Tooltip title="Gere um relatório completo das vulnerabilidades encontradas">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>,
        <Button key="reset" onClick={handleReset} disabled={loading}>
          Limpar
        </Button>,
        <Button
          key="generate"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          icon={<DownloadOutlined />}
        >
          {loading ? `Gerando... ${progresso}%` : 'Gerar Relatório'}
        </Button>
      ]}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message={`Relatório será gerado para: ${clienteNome}`}
          type="info"
          showIcon
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          periodo: [dayjs().subtract(30, 'days'), dayjs()],
          severidades: [SeverityLevel.HIGH, SeverityLevel.MEDIUM, SeverityLevel.LOW, SeverityLevel.INFO],
          incluirDetalhes: true,
          incluirGraficos: true,
          incluirRecomendacoes: true,
          incluirResumoExecutivo: true,
          incluirIndice: true,
          incluirEstatisticas: true,
          limiteDetalhes: 100
        }}
      >
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item label="Período Pré-definido">
              <Select
                placeholder="Selecione um período"
                onChange={handlePeriodoChange}
                disabled={loading}
              >
                {periodosPreDefinidos.map(periodo => (
                  <Option key={periodo.value} value={periodo.value}>
                    {periodo.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="periodo"
              label="Período de Análise"
              rules={[{ required: true, message: 'Selecione o período' }]}
            >
              <RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Data inicial', 'Data final']}
                disabled={loading || !periodoPersonalizado}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="severidades"
              label="Severidades"
              rules={[{ required: true, message: 'Selecione pelo menos uma severidade' }]}
            >
              <Select
                mode="multiple"
                placeholder="Selecione as severidades"
                style={{ width: '100%' }}
                disabled={loading}
              >
                <Option value={SeverityLevel.HIGH}>Alta</Option>
                <Option value={SeverityLevel.MEDIUM}>Média</Option>
                <Option value={SeverityLevel.LOW}>Baixa</Option>
                <Option value={SeverityLevel.INFO}>Informativa</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Filtros Avançados">
              <Button
                type={opcesAvancadas ? 'primary' : 'default'}
                icon={<SettingOutlined />}
                onClick={() => setOpcesAvancadas(!opcesAvancadas)}
                disabled={loading}
              >
                {opcesAvancadas ? 'Ocultar' : 'Mostrar'} Filtros
              </Button>
            </Form.Item>
          </Col>
        </Row>

        {opcesAvancadas && (
          <>
            <Divider orientation="left">Filtros Avançados</Divider>
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="host"
                  label="Filtrar por Host"
                >
                  <Select
                    placeholder="Todos os hosts"
                    allowClear
                    disabled={loading}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    notFoundContent={hostsLimpos.length === 0 ? "Nenhum host disponível" : "Host não encontrado"}
                  >
                    {hostsLimpos.map(host => (
                      <Option key={host} value={host} title={host}>
                        {host}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="cvssMinimo"
                  label="Score CVSS Mínimo"
                >
                  <Select
                    placeholder="Todos os scores"
                    allowClear
                    disabled={loading}
                  >
                    <Option value={0}>0.0+ (Todos)</Option>
                    <Option value={0.1}>0.1+ (Baixo)</Option>
                    <Option value={4}>4.0+ (Médio)</Option>
                    <Option value={7}>7.0+ (Alto)</Option>
                    <Option value={9}>9.0+ (Crítico)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="cveEspecifico"
                  label="CVE Específico"
                >
                  <Input
                    placeholder="Ex: CVE-2023-1234 ou 'pendente' para CVEs não identificados"
                    allowClear
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="portaEspecifica"
                  label="Porta Específica"
                >
                  <Input
                    placeholder="Ex: 80, 443"
                    allowClear
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="limiteDetalhes"
                  label={
                    <Space>
                      Limite de Vulnerabilidades Detalhadas
                      <Tooltip title="Define quantas vulnerabilidades serão listadas em detalhes no PDF. As mais críticas são priorizadas por score CVSS e severidade.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Select
                    placeholder="Selecione o limite"
                    disabled={loading}
                  >
                    <Option value={10}>
                      <Space>
                        <span>10 vulnerabilidades</span>
                        <Text type="secondary" style={{ fontSize: '11px' }}>(~5 páginas)</Text>
                      </Space>
                    </Option>
                    <Option value={25}>
                      <Space>
                        <span>25 vulnerabilidades</span>
                        <Text type="secondary" style={{ fontSize: '11px' }}>(~12 páginas)</Text>
                      </Space>
                    </Option>
                    <Option value={50}>
                      <Space>
                        <span>50 vulnerabilidades</span>
                        <Text type="secondary" style={{ fontSize: '11px' }}>(~25 páginas)</Text>
                      </Space>
                    </Option>
                    <Option value={100}>
                      <Space>
                        <span>100 vulnerabilidades</span>
                        <Text type="secondary" style={{ fontSize: '11px' }}>(~50 páginas) - Padrão</Text>
                      </Space>
                    </Option>
                    <Option value={200}>
                      <Space>
                        <span>200 vulnerabilidades</span>
                        <Text type="secondary" style={{ fontSize: '11px' }}>(~100 páginas)</Text>
                      </Space>
                    </Option>
                    <Option value={-1}>
                      <Space>
                        <span>Todas as vulnerabilidades</span>
                        <Text type="secondary" style={{ fontSize: '11px', color: '#ff4d4f' }}>(pode gerar arquivo muito grande)</Text>
                      </Space>
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <div style={{ paddingTop: '30px' }}>
                  <Alert
                    message="Dica"
                    description="As vulnerabilidades são ordenadas automaticamente por criticidade (score CVSS, severidade e data)."
                    type="info"
                    showIcon
                    style={{ fontSize: '11px' }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}

        <Divider orientation="left">Conteúdo do Relatório</Divider>
        
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item name="incluirResumoExecutivo" valuePropName="checked">
              <Checkbox disabled={loading}>
                Incluir Resumo Executivo
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirIndice" valuePropName="checked">
              <Checkbox disabled={loading}>
                Incluir Índice/Sumário
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirEstatisticas" valuePropName="checked">
              <Checkbox disabled={loading}>
                Incluir Estatísticas Gerais
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirGraficos" valuePropName="checked">
              <Checkbox disabled={loading}>
                Incluir Gráficos e Charts
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirDetalhes" valuePropName="checked">
              <Checkbox disabled={loading}>
                Incluir Detalhes das Vulnerabilidades
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="incluirRecomendacoes" valuePropName="checked">
              <Checkbox disabled={loading}>
                Incluir Recomendações
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {loading && (
          <div style={{ marginTop: 16 }}>
            <Text>Gerando relatório...</Text>
            <Progress 
              percent={progresso} 
              status="active" 
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Isso pode levar alguns minutos dependendo da quantidade de dados
            </Text>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default CSARelatorioModal;